import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaPaperPlane,
  FaPenNib,
  FaSignOutAlt,
} from "react-icons/fa";

import api from "../api/axios";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";

const emptyBlog = {
  title: "",
  category: "",
  description: "",
  content: "",
  image: "",
  readTime: "5 Min Read",
};

function AdminBlog() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(emptyBlog);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const successTimer = useRef(null);

  // Without this, the success banner's timer fires after navigation away.
  useEffect(() => () => clearTimeout(successTimer.current), []);

  const handleChange = (e) =>
    setBlog({ ...blog, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError("");
    setSubmitting(true);

    try {
      await api.post("/blogs", blog);
      setSuccess(true);
      setBlog(emptyBlog);

      clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      if (err.response?.status === 401) {
        // Session expired or invalid — send back to login
        logout();
        navigate("/login", { replace: true });
        return;
      }
      setError(
        err.response?.status === 403
          ? "You don't have permission to do that."
          : err.response?.data?.message ||
              "Failed to add blog. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <AdminShell
      icon={<FaPenNib />}
      title="Create New Blog"
      subtitle={user ? `Signed in as ${user.email}` : undefined}
      maxWidth="1140px"
      actions={
        <button type="button" onClick={handleLogout} className="adm-btn adm-btn--ghost">
          <FaSignOutAlt />
          Logout
        </button>
      }
    >
      <style>{`
        .abg-panel { padding: clamp(22px, 3vw, 32px); }
        .abg-side { position: relative; }
        @media (min-width: 992px) { .abg-side { position: sticky; top: 96px; } }
        .abg-side__h {
          font-size: .74rem; font-weight: 700; letter-spacing: .1em;
          text-transform: uppercase; color: #94a3b8; margin-bottom: 12px;
        }
        /* Mirrors the real listing card so the author sees what ships */
        .abg-preview {
          border-radius: 20px; padding: 24px 22px;
          background: #fff; border: 1px solid #f0e4dd;
          box-shadow: 0 8px 26px rgba(194,65,12,.1);
        }
        .abg-preview__img {
          width: 100%; height: 150px; object-fit: cover;
          border-radius: 14px; margin-bottom: 16px; display: block;
        }
        .abg-preview__cat {
          display: inline-block; background: #ffe9e0; color: #c2410c;
          padding: 5px 13px; border-radius: 999px; font-size: .72rem; font-weight: 700;
        }
        .abg-preview__title {
          color: #0f172a; font-weight: 700; font-size: 1.1rem;
          line-height: 1.35; margin: 14px 0 10px;
        }
        .abg-preview__meta {
          display: flex; flex-wrap: wrap; gap: 14px;
          color: #94a3b8; font-size: .8rem; margin-bottom: 12px;
        }
        .abg-preview__meta span { display: inline-flex; align-items: center; gap: 6px; }
        .abg-preview__meta svg { color: #ff9066; }
        .abg-preview__desc { color: #64748b; font-size: .9rem; line-height: 1.7; margin: 0; }
        .abg-preview__ph { color: #cbd5e1; font-style: italic; }
        .abg-count { color: #94a3b8; font-size: .76rem; text-align: right; margin-top: 5px; }
      `}</style>

      <div aria-live="polite">
        {success && (
          <Alert variant="success">Blog published successfully!</Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
      </div>

      <Row className="g-4">
        <Col lg={7}>
          <div className="adm-panel abg-panel adm-form">
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="ab-title">
                <Form.Label>Blog Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={blog.title}
                  onChange={handleChange}
                  placeholder="A clear, specific headline"
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="ab-category">
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={blog.category}
                      onChange={handleChange}
                      placeholder="e.g. Digital Transformation"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3" controlId="ab-readtime">
                    <Form.Label>Read Time</Form.Label>
                    <Form.Control
                      type="text"
                      name="readTime"
                      value={blog.readTime}
                      onChange={handleChange}
                      placeholder="5 Min Read"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="ab-image">
                <Form.Label>Image URL</Form.Label>
                <Form.Control
                  type="url"
                  name="image"
                  value={blog.image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                />
                <div className="adm-hint">
                  Optional. Shown at the top of the article and in the preview.
                </div>
              </Form.Group>

              <Form.Group className="mb-3" controlId="ab-description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={blog.description}
                  onChange={handleChange}
                  placeholder="One or two sentences — this is the card summary."
                  required
                />
                <div className="abg-count">{blog.description.length} characters</div>
              </Form.Group>

              <Form.Group className="mb-4" controlId="ab-content">
                <Form.Label>Blog Content</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={12}
                  name="content"
                  value={blog.content}
                  onChange={handleChange}
                  placeholder="The full article. Line breaks are preserved."
                  required
                />
                <div className="abg-count">{blog.content.length} characters</div>
              </Form.Group>

              {/* `submitting` guard — the old button could be double-clicked
                  and publish the same post twice. */}
              <button type="submit" className="adm-btn adm-btn--solid" disabled={submitting}>
                <FaPaperPlane />
                {submitting ? "Publishing…" : "Publish Blog"}
              </button>
            </Form>
          </div>
        </Col>

        <Col lg={5}>
          <div className="abg-side">
            <div className="abg-side__h">Live preview</div>
            <div className="abg-preview">
              {blog.image && (
                <img
                  src={blog.image}
                  alt=""
                  className="abg-preview__img"
                  // A typo'd URL shouldn't leave a broken-image icon
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}

              <span className="abg-preview__cat">
                {blog.category || "Category"}
              </span>

              <h2 className="abg-preview__title">
                {blog.title || (
                  <span className="abg-preview__ph">Your title appears here</span>
                )}
              </h2>

              <div className="abg-preview__meta">
                <span>
                  <FaCalendarAlt />
                  {new Date().toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                {blog.readTime && (
                  <span>
                    <FaClock />
                    {blog.readTime}
                  </span>
                )}
              </div>

              <p className="abg-preview__desc">
                {blog.description || (
                  <span className="abg-preview__ph">
                    Your description appears here, exactly as readers will see it
                    on the blog listing.
                  </span>
                )}
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </AdminShell>
  );
}

export default AdminBlog;
